import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { SectionHint } from "@/components/SectionHint";
import { WantEventButton } from "@/components/ExpertRequestForm";
import { ER_COPY } from "@/lib/expert-requests-copy";
import { PageHeader, SegmentedTabs } from "@/components/kit";
import { showExpertVenueCard } from "@/lib/platform-counts";
import { citiesLabel, matchesMyCity, REQUEST_TTL_DAYS } from "@/lib/expert-requests-logic";
import { TURKEY_CITIES } from "@/lib/people-constants";
import { fetchPublishedEventCounts, type ExpertRequestRow } from "@/lib/expert-requests-data";

type Tab = "mine" | "requests" | "organizers";

export const Route = createFileRoute("/_authenticated/expert-requests")({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab; create?: boolean } => ({
    tab: s.tab === "mine" || s.tab === "requests" || s.tab === "organizers" ? s.tab : undefined,
    create: s.create === true || s.create === "1" || s.create === "true" ? true : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Запросы экспертов — TurkeyDrive" },
      {
        name: "description",
        content: "Эксперты ищут площадки для мероприятий, организаторы находят ведущих.",
      },
      { property: "og:title", content: "Запросы экспертов — TurkeyDrive" },
      {
        property: "og:description",
        content: "Эксперты ищут площадки для мероприятий, организаторы находят ведущих.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ExpertRequestsPage,
});

function ExpertRequestsPage() {
  const { user } = useAuth();
  const uid = user?.id;
  const { tab: tabParam, create } = Route.useSearch();
  const navigate = useNavigate({ from: "/expert-requests" });
  const qc = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ["hosts-for-experts", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("city, hosts_for_experts, hosts_for_experts_note")
        .eq("user_id", uid!)
        .maybeSingle();
      return data;
    },
  });
  const { data: isExpert = false } = useQuery({
    queryKey: ["expert-fields-complete", uid],
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.rpc("profile_expert_fields_complete", { p_user_id: uid! });
      return showExpertVenueCard(data as boolean | null);
    },
  });
  const tab: Tab = tabParam ?? (create ? "mine" : me?.hosts_for_experts ? "requests" : "mine");
  const setTab = (t: Tab) => void navigate({ search: { tab: t }, replace: true });

  return (
    <div className="min-h-screen min-w-0 bg-white">
      <main className="mx-auto min-w-0 max-w-[720px] px-4 py-6 md:py-10">
        <PageHeader title={ER_COPY.pageTitle} subtitle={ER_COPY.pageText} />
        <SectionHint hint="expert_requests" className="mt-4" />
        <div className="mt-4">
          <SegmentedTabs<Tab>
            value={tab}
            onChange={setTab}
            items={[
              { key: "mine", label: ER_COPY.tabMine },
              { key: "requests", label: ER_COPY.tabRequests },
              { key: "organizers", label: ER_COPY.tabOrganizers },
            ]}
          />
        </div>
        {tab === "mine" && isExpert && (
          <div className="mt-4">
            <WantEventButton
              defaultOpen={!!create}
              className="cta-solid inline-flex h-11 w-full items-center justify-center rounded-full px-5 text-sm font-semibold"
              hint
            >
              {ER_COPY.venueCardButton}
            </WantEventButton>
          </div>
        )}
        <div className="mt-4">
          {!uid || !me ? (
            <p className="text-sm text-td-muted">Загружаем…</p>
          ) : tab === "mine" ? (
            <MineTab uid={uid} />
          ) : tab === "requests" ? (
            me.hosts_for_experts ? (
              <RequestsTab uid={uid} myCity={me.city} />
            ) : (
              <div className="rounded-2xl bg-td-soft/60 p-4">
                <p className="text-sm text-td-dark">{ER_COPY.hostsOffTitle}</p>
                <button
                  type="button"
                  className="mt-3 inline-flex h-11 items-center rounded-full bg-td-teal px-5 text-sm font-semibold text-white hover:bg-td-teal/90"
                  onClick={async () => {
                    const { error } = await supabase
                      .from("profiles")
                      .update({ hosts_for_experts: true })
                      .eq("user_id", uid);
                    if (error) return void toast.error(error.message);
                    toast.success(ER_COPY.hostsOn);
                    await Promise.all([
                      qc.invalidateQueries({ queryKey: ["hosts-for-experts", uid] }),
                      qc.invalidateQueries({ queryKey: ["first-steps", uid] }),
                    ]);
                  }}
                >
                  {ER_COPY.hostsOffButton}
                </button>
              </div>
            )
          ) : (
            <OrganizersTab />
          )}
        </div>
      </main>
    </div>
  );
}

function MineTab({ uid }: { uid: string }) {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["expert-requests", "mine", uid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expert_event_requests")
        .select("*, expert_event_offers(count)")
        .eq("expert_user_id", uid)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
  if (isLoading) return <p className="text-sm text-td-muted">Загружаем…</p>;
  if (!data.length)
    return (
      <p className="rounded-2xl bg-td-soft/60 p-4 text-sm text-td-dark">{ER_COPY.mineEmpty}</p>
    );
  return (
    <ul className="space-y-3">
      {data.map((r) => {
        const offers = (r.expert_event_offers as unknown as { count: number }[])?.[0]?.count ?? 0;
        return (
          <li key={r.id} className="rounded-2xl border border-td-line bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <Link
                to="/expert-requests/$requestId"
                params={{ requestId: r.id }}
                className="min-w-0 break-words font-semibold text-td-dark hover:underline"
              >
                {r.topic}
              </Link>
              <span
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs ${r.status === "open" ? "bg-td-soft text-td-dark" : "bg-muted text-td-muted"}`}
              >
                {r.status === "open" ? ER_COPY.statusOpen : ER_COPY.statusClosed}
              </span>
            </div>
            <p className="mt-1 text-sm text-td-muted">
              {citiesLabel(r.cities)} · {ER_COPY.offersCount(offers)}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link
                to="/expert-requests/$requestId"
                params={{ requestId: r.id }}
                className="text-sm font-semibold text-td-teal underline underline-offset-2"
              >
                {ER_COPY.offersTitle}
              </Link>
              {r.status === "open" && (
                <button
                  type="button"
                  className="text-sm text-td-muted underline underline-offset-2"
                  onClick={async () => {
                    const { error } = await supabase
                      .from("expert_event_requests")
                      .update({ status: "closed" })
                      .eq("id", r.id);
                    if (error) return void toast.error(error.message);
                    toast.success(ER_COPY.closed);
                    await qc.invalidateQueries({ queryKey: ["expert-requests"] });
                  }}
                >
                  {ER_COPY.close}
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RequestsTab({ uid, myCity }: { uid: string; myCity: string | null }) {
  const [all, setAll] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["expert-requests", "open", uid],
    queryFn: async () => {
      const since = new Date(Date.now() - REQUEST_TTL_DAYS * 86_400_000).toISOString();
      const { data, error } = await supabase
        .from("expert_event_requests")
        .select("*")
        .eq("status", "open")
        .gte("created_at", since)
        .neq("expert_user_id", uid)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const rows = (data ?? []) as ExpertRequestRow[];
      const ids = [...new Set(rows.map((r) => r.expert_user_id))];
      const { data: profs } = ids.length
        ? await supabase.from("profiles").select("user_id, full_name").in("user_id", ids)
        : { data: [] as { user_id: string; full_name: string }[] };
      const names = Object.fromEntries((profs ?? []).map((p) => [p.user_id, p.full_name]));
      return { rows, names };
    },
  });
  const list = useMemo(
    () => (data?.rows ?? []).filter((r) => all || matchesMyCity(r.cities, myCity)),
    [data, all, myCity],
  );
  if (isLoading) return <p className="text-sm text-td-muted">Загружаем…</p>;
  return (
    <>
      <label className="mb-3 flex items-center gap-2 text-sm text-td-dark">
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-td-teal)]"
          checked={all}
          onChange={(e) => setAll(e.target.checked)}
        />
        {ER_COPY.allCities}
      </label>
      {!list.length ? (
        <p className="rounded-2xl bg-td-soft/60 p-4 text-sm text-td-dark">
          {all ? ER_COPY.requestsEmptyAll : ER_COPY.requestsEmpty}
        </p>
      ) : (
        <ul className="space-y-3">
          {list.map((r) => (
            <li key={r.id}>
              <Link
                to="/expert-requests/$requestId"
                params={{ requestId: r.id }}
                className="block rounded-2xl border border-td-line bg-white p-4 hover:bg-td-soft/30"
              >
                <p className="break-words font-semibold text-td-dark">{r.topic}</p>
                <p className="mt-1 text-sm text-td-muted">
                  {[
                    citiesLabel(r.cities),
                    r.period,
                    r.audience_size ? `${r.audience_size} чел.` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {r.conditions && (
                  <p className="mt-1 break-words text-sm text-td-dark">{r.conditions}</p>
                )}
                <p className="mt-2 text-xs text-td-muted">
                  {ER_COPY.expertLabel}: {data?.names[r.expert_user_id] ?? "—"}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function OrganizersTab() {
  const [city, setCity] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["expert-hosts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, city, photo_url, hosts_for_experts_note")
        .eq("hosts_for_experts", true)
        .is("deletion_requested_at", null)
        .limit(300);
      if (error) throw error;
      const rows = data ?? [];
      const counts = await fetchPublishedEventCounts(rows.map((r) => r.user_id));
      return { rows, counts };
    },
  });
  const list = (data?.rows ?? []).filter((r) => !city || r.city === city);
  if (isLoading) return <p className="text-sm text-td-muted">Загружаем…</p>;
  return (
    <>
      <select
        value={city}
        onChange={(e) => setCity(e.target.value)}
        className="mb-3 h-10 w-full rounded-xl border border-td-line bg-white px-3 text-sm text-td-dark sm:w-auto"
      >
        <option value="">{ER_COPY.cityFilterAll}</option>
        {TURKEY_CITIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      {!list.length ? (
        <p className="rounded-2xl bg-td-soft/60 p-4 text-sm text-td-dark">
          {ER_COPY.organizersEmpty}
        </p>
      ) : (
        <ul className="space-y-3">
          {list.map((p) => (
            <li
              key={p.user_id}
              className="flex min-w-0 gap-3 rounded-2xl border border-td-line bg-white p-4"
            >
              {p.photo_url ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={p.photo_url}
                  alt={p.full_name}
                  className="size-12 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-td-soft font-semibold text-td-dark">
                  {(p.full_name || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-td-dark">{p.full_name}</p>
                <p className="text-xs text-td-muted">
                  {[p.city, ER_COPY.eventsCount(data?.counts[p.user_id] ?? 0)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {p.hosts_for_experts_note && (
                  <p className="mt-1 break-words text-sm text-td-dark">
                    {p.hosts_for_experts_note}
                  </p>
                )}
                <Link
                  to="/people/$userId"
                  params={{ userId: p.user_id }}
                  className="mt-2 inline-block text-sm font-semibold text-td-teal underline underline-offset-2"
                >
                  {ER_COPY.openProfile}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
